import {Component, OnInit} from '@angular/core';
import {Constant} from '../../../../global/util/Constant';
import {Partner} from '../../../model/Partner';
import {Enterprise} from '../../../model/Enterprise';
import {DocumentService} from '../../../service/document.service';
import {AuthService} from '../../../service/auth/auth.service';
import {ApplicationService} from '../../../service/application.service';
import {Router} from '@angular/router';
import {LocalStorage} from '../../../../global/util/LocalStorage';
import {Dialog} from '../../../../global/util/Dialog';
import {InformType} from '../../../../global/util/enum/InformType';
import HttpStatusCode from '../../../../global/util/HttpStatusCode';
import {GenericHead} from '../../../model/GenericHead';
import {Parameter} from '../../../model/Parameter';
import {Generic} from '../../../model/Generic';
import {ParameterService} from '../../../service/parameter.service';
import {TranslateService} from '@ngx-translate/core';
import {EnterpriseService} from '../../../service/enterprise.service';

@Component({
  selector: 'app-parameter-board',
  templateUrl: './parameter-board.component.html',
  styleUrls: ['./parameter-board.component.css']
})
export class ParameterBoardComponent implements OnInit {
  public readonly ROLE_SUPERADMIN = Constant.ROLE_SUPERADMIN;
  public readonly ROLE_PARTNER = Constant.ROLE_PARTNER;
  public readonly reviewerKey = 'iofesign:outside:documents:reviewer:person-id';
  public partners: Partner[] = [];
  public enterprises: Enterprise[] = [];
  public genericHead = new GenericHead();
  public loading = false;
  public parameters: Parameter[] = [];
  public assignedParameters: Parameter[] = [];
  public originalAssignedParameters: Parameter[] = [];
  public searchTerm = '';
  public onlyAssigned = false;
  private requestVersion = 0;

  constructor(private documentService: DocumentService,
              public authenticationService: AuthService,
              private applicationService: ApplicationService,
              private parameterService: ParameterService,
              private enterpriseService: EnterpriseService,
              private router: Router,
              private translate: TranslateService) {
  }

  ngOnInit(): void {
    this.loadDefaultData();
  }

  public hasRole(name: string): boolean {
    return this.authenticationService.hasRole(name);
  }

  public get enterpriseName(): string {
    const enterprise = this.enterprises.find(value => value.id === this.genericHead.enterpriseId);
    return enterprise ? enterprise.name : '';
  }

  public get assignedCount(): number {
    return this.assignedParameters.filter(value => value.active).length;
  }

  public get pendingCount(): number {
    const ids = new Set<number>([
      ...this.assignedParameters.map(value => value.id),
      ...this.originalAssignedParameters.map(value => value.id)
    ]);
    let count = 0;
    ids.forEach(id => {
      const current = this.assignedParameters.find(value => value.id === id);
      const original = this.originalAssignedParameters.find(value => value.id === id);
      const currentActive = !!current && !!current.active;
      const originalActive = !!original && !!original.active;
      if (currentActive !== originalActive ||
          (currentActive && (current.actualValue || '') !== (original && original.actualValue || ''))) {
        count++;
      }
    });
    return count;
  }

  public get visibleParameters(): Parameter[] {
    const term = this.searchTerm.trim().toLocaleLowerCase();
    return this.parameters.filter(parameter => {
      if (this.onlyAssigned && !this.isAssigned(parameter)) {
        return false;
      }
      return !term || [parameter.key, parameter.description, parameter.value]
        .some(value => (value || '').toLocaleLowerCase().includes(term));
    });
  }

  public isAssigned(parameter: Parameter): boolean {
    const assignment = this.getAssignment(parameter);
    return !!assignment && !!assignment.active;
  }

  public getAssignment(parameter: Parameter): Parameter {
    return this.assignedParameters.find(value => value.id === parameter.id);
  }

  public toggleAssignment(event: any, parameter: Parameter): void {
    const active = !!event.target.checked;
    const current = this.getAssignment(parameter);
    if (current) {
      current.active = active;
    } else if (active) {
      this.assignedParameters.push({...parameter, active: true, actualValue: null});
    }
  }

  public setActualValue(parameter: Parameter, value: string): void {
    const assignment = this.getAssignment(parameter);
    if (assignment) {
      assignment.actualValue = value;
    }
  }

  public resetChanges(): void {
    this.assignedParameters = this.originalAssignedParameters.map(value => ({...value}));
  }

  public reloadEnterprises(): void {
    this.getEnterprises(this.genericHead.partnerId);
  }

  public loadAssignedParams(): void {
    const enterpriseId = this.genericHead.enterpriseId;
    const version = ++this.requestVersion;
    this.loading = true;
    const url = Constant.ROOT_API_V1.concat('/enterprises/', String(enterpriseId), '/parameters/assigned');
    this.documentService.get(url).subscribe((response: any) => {
      if (version !== this.requestVersion) { return; }
      this.setAssignments(response._embedded.assignedParameters || []);
      this.loading = false;
    }, error => {
      if (version !== this.requestVersion) { return; }
      this.loading = false;
      this.processError(error);
    });
  }

  public save(): void {
    if (this.loading || !this.genericHead.enterpriseId || this.pendingCount === 0) { return; }
    const reviewer = this.assignedParameters.find(value => value.active && value.key === this.reviewerKey);
    if (reviewer && !/^[1-9][0-9]*$/.test((reviewer.actualValue || '').trim())) {
      Dialog.show(InformType.Danger, this.translate.instant('TITLE.PARAMETER-TABLE'),
        this.translate.instant('PARAMETER_BOARD.REVIEWER_HELP'));
      return;
    }
    const enterpriseId = this.genericHead.enterpriseId;
    const generic = new Generic();
    generic.assignedParameters = this.assignedParameters
      .filter(value => value.active || this.originalAssignedParameters.some(original => original.id === value.id))
      .map(value => ({...value, actualValue: value.actualValue == null ? null : value.actualValue.trim()}));
    this.loading = true;
    const url = Constant.ROOT_API_V1.concat('/enterprises/', String(enterpriseId), '/parameters');
    this.parameterService.update(url, generic).subscribe(() => {
      Dialog.show(InformType.Success, this.translate.instant('TITLE.PARAMETER-TABLE'));
      this.loadAssignedParams();
    }, error => {
      this.loading = false;
      this.processError(error);
    });
  }

  public cancel(): void {
    this.router.navigate([this.hasRole(this.ROLE_SUPERADMIN) ? 'enterprise' : 'document']);
  }

  private loadDefaultData(): void {
    this.enterprises = LocalStorage.getDefaultEnterprise() || [];
    if (this.hasRole(this.ROLE_SUPERADMIN)) {
      this.partners = LocalStorage.getPartners() || [];
      if (this.partners.length) {
        this.genericHead.partnerId = this.partners[0].id;
        this.getEnterprises(this.genericHead.partnerId);
      }
    } else if (this.hasRole(this.ROLE_PARTNER)) {
      this.getEnterprises(LocalStorage.getPartnerId());
    } else {
      Dialog.show(InformType.Danger, this.translate.instant('TITLE.INITIAL-DATA-RELOAD'));
    }
  }

  private getEnterprises(partnerId: number): void {
    const version = ++this.requestVersion;
    this.loading = true;
    this.enterpriseService.getEnterprisesByPartnerModal(partnerId).subscribe(enterprises => {
      if (version !== this.requestVersion) { return; }
      this.enterprises = (enterprises || []).sort((left, right) =>
        (left.name || '').localeCompare(right.name || ''));
      this.genericHead.enterpriseId = this.enterprises.length ? this.enterprises[0].id : null;
      this.assignedParameters = [];
      this.originalAssignedParameters = [];
      if (this.genericHead.enterpriseId) {
        this.initialData();
      } else {
        this.loading = false;
      }
    }, error => {
      if (version !== this.requestVersion) { return; }
      this.loading = false;
      this.processError(error);
    });
  }

  private initialData(): void {
    const version = ++this.requestVersion;
    const enterpriseId = this.genericHead.enterpriseId;
    this.loading = true;
    const url = Constant.ROOT_API_V1.concat('/parameters?enterprise=', String(enterpriseId), '&scope=no-global');
    this.documentService.get(url).subscribe((response: any) => {
      if (version !== this.requestVersion) { return; }
      this.parameters = response._embedded.parameters || [];
      this.setAssignments(response._embedded.assignedParameters || []);
      this.loading = false;
    }, error => {
      if (version !== this.requestVersion) { return; }
      this.loading = false;
      this.processError(error);
    });
  }

  private setAssignments(assignments: Parameter[]): void {
    this.assignedParameters = assignments.map(value => ({...value, active: true}));
    this.originalAssignedParameters = this.assignedParameters.map(value => ({...value}));
  }

  private processError(error: any): void {
    const title = this.translate.instant('TITLE.PARAMETER-TABLE');
    const message = error && error.error && (error.error.message || error.error.error);
    if (error && error.status === HttpStatusCode.FORBIDDEN) {
      Dialog.show(InformType.Info, title, message || this.translate.instant('MESSAGE.SESSION-EXPIRED'));
    } else if (error && error.status === HttpStatusCode.UNAUTHORIZED) {
      Dialog.show(InformType.Info, this.translate.instant('TITLE.SESSION-EXPIRED'),
        this.translate.instant('MESSAGE.SESSION-EXPIRED'));
      this.applicationService.setUserLoggedIn(false);
      this.router.navigate(['login']);
    } else {
      Dialog.show(InformType.Danger, title, message || undefined);
    }
  }
}
